import { useState, useCallback } from "react";

interface JsonTreeProps {
  data: unknown;
  depth?: number;
  keyName?: string;
}

export default function JsonTree({ data, depth = 0, keyName }: JsonTreeProps) {
  if (data === null) {
    return (
      <span className="json-value json-null">
        {keyName ? <JsonKey name={keyName} /> : null}null
      </span>
    );
  }
  if (typeof data === "boolean") {
    return (
      <span className="json-value json-boolean">
        {keyName ? <JsonKey name={keyName} /> : null}
        {data ? "true" : "false"}
      </span>
    );
  }
  if (typeof data === "number") {
    return (
      <span className="json-value json-number">
        {keyName ? <JsonKey name={keyName} /> : null}
        {String(data)}
      </span>
    );
  }
  if (typeof data === "string") {
    return (
      <span className="json-value json-string">
        {keyName ? <JsonKey name={keyName} /> : null}
        "{data}"
      </span>
    );
  }
  if (Array.isArray(data)) {
    return <JsonArray data={data} depth={depth} keyName={keyName} />;
  }
  if (typeof data === "object") {
    return <JsonObject data={data as Record<string, unknown>} depth={depth} keyName={keyName} />;
  }
  return <span>{String(data)}</span>;
}

function JsonKey({ name }: { name: string }) {
  return <span className="json-key">"{name}"</span>;
}

function JsonArray({
  data,
  depth,
  keyName,
}: {
  data: unknown[];
  depth: number;
  keyName?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  const isEmpty = data.length === 0;

  if (isEmpty) {
    return (
      <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
        {keyName ? <JsonKey name={keyName} /> : null}
        <span className="json-bracket">[]</span>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
        <span className="json-toggle" onClick={toggle}>
          &#x25B6;
        </span>
        {keyName ? <JsonKey name={keyName} /> : null}
        <span className="json-bracket">[</span>
        <span className="json-collapsed"> {data.length} items </span>
        <span className="json-bracket">]</span>
      </div>
    );
  }

  return (
    <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
      <span className="json-toggle" onClick={toggle}>
        &#x25BC;
      </span>
      {keyName ? <JsonKey name={keyName} /> : null}
      <span className="json-bracket">[</span>
      <div className="json-children">
        {data.map((item, index) => (
          <div key={index} className="json-row">
            <JsonTree data={item} depth={depth + 1} />
            {index < data.length - 1 && ","}
          </div>
        ))}
      </div>
      <span className="json-bracket">]</span>
    </div>
  );
}

function JsonObject({
  data,
  depth,
  keyName,
}: {
  data: Record<string, unknown>;
  depth: number;
  keyName?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  const entries = Object.entries(data);
  const isEmpty = entries.length === 0;

  if (isEmpty) {
    return (
      <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
        {keyName ? <JsonKey name={keyName} /> : null}
        <span className="json-bracket">{'{'}{'}'}</span>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
        <span className="json-toggle" onClick={toggle}>
          &#x25B6;
        </span>
        {keyName ? <JsonKey name={keyName} /> : null}
        <span className="json-bracket">{'{'}</span>
        <span className="json-collapsed"> {entries.length} keys </span>
        <span className="json-bracket">{'}'}</span>
      </div>
    );
  }

  return (
    <div className="json-entry" style={{ paddingLeft: depth * 20 }}>
      <span className="json-toggle" onClick={toggle}>
        &#x25BC;
      </span>
      {keyName ? <JsonKey name={keyName} /> : null}
      <span className="json-bracket">{'{'}</span>
      <div className="json-children">
        {entries.map(([key, value], index) => (
          <div key={key} className="json-row">
            <JsonTree data={value} depth={depth + 1} keyName={key} />
            {index < entries.length - 1 && ","}
          </div>
        ))}
      </div>
      <span className="json-bracket">{'}'}</span>
    </div>
  );
}
